// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/dropdown.js
import { createPopper } from "@popperjs/core"
import BaseComponent from "./base_component"

const openDropdowns = new Set()

export default class Dropdown extends BaseComponent {
  static componentName = "dropdown"
  static defaults = { autoClose: true, display: "dynamic", offset: [0, 2] }

  constructor(element, config = {}) {
    super(element, config)
    this.menu =
      element
        .closest(".dropdown, .btn-group, .dropup, .dropend, .dropstart")
        ?.querySelector(":scope > .dropdown-menu") || element.nextElementSibling
    this.listen(element, "keydown", (event) => this.keydown(event))
    if (this.menu) this.listen(this.menu, "keydown", (event) => this.keydown(event))
    this.listen(document, "click", (event) => this.clear(event))
    this.listen(document, "keyup", (event) => {
      if (event.key === "Tab") this.clear(event)
    })
  }

  keydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || !["ArrowUp", "ArrowDown", "Escape"].includes(event.key))
      return
    if (/input|textarea/i.test(event.target.tagName) && event.key !== "Escape") return
    event.preventDefault()
    event.stopPropagation()
    if (event.key === "Escape") {
      if (this.menu?.classList.contains("show")) {
        this.hide()
        this.element.focus()
      }
      return
    }
    this.show()
    if (!this.menu?.classList.contains("show")) return
    const items = [...this.menu.querySelectorAll(".dropdown-item:not(.disabled):not(:disabled)")].filter(
      (item) => item.getClientRects().length,
    )
    const index = items.indexOf(event.target)
    const next =
      index < 0
        ? event.key === "ArrowDown"
          ? 0
          : items.length - 1
        : Math.max(0, Math.min(items.length - 1, index + (event.key === "ArrowDown" ? 1 : -1)))
    items[next]?.focus()
  }

  clear(event) {
    if (event.button === 2 || this.config.autoClose === false || this.element.contains(event.target)) return
    const inside = this.menu?.contains(event.target)
    if ((this.config.autoClose === "inside" && !inside) || (this.config.autoClose === "outside" && inside)) return
    if (inside && (event.type === "keyup" || /input|select|option|textarea|form/i.test(event.target.tagName))) return
    this.hide(event)
  }

  show() {
    if (
      !this.menu ||
      this.menu.classList.contains("show") ||
      this.element.disabled ||
      this.element.classList.contains("disabled")
    )
      return
    const detail = { relatedTarget: this.element }
    if (this.emit("show", detail, { cancelable: true }).defaultPrevented) return
    Dropdown.clearMenus(this.element)
    if (this.config.display !== "static")
      this.popper = createPopper(this.element, this.menu, {
        placement: this.placement(),
        modifiers: [{ name: "offset", options: { offset: this.config.offset } }],
      })
    this.element.classList.add("show")
    this.menu.classList.add("show")
    this.element.setAttribute("aria-expanded", "true")
    openDropdowns.add(this)
    this.emit("shown", detail)
  }

  hide(clickEvent) {
    if (!this.menu?.classList.contains("show")) return
    const detail = { relatedTarget: this.element, ...(clickEvent ? { clickEvent } : {}) }
    if (this.emit("hide", detail, { cancelable: true }).defaultPrevented) return
    this.reset()
    this.emit("hidden", detail)
  }

  reset() {
    this.popper?.destroy()
    this.popper = null
    this.element.classList.remove("show")
    this.menu?.classList.remove("show")
    this.element.setAttribute("aria-expanded", "false")
    openDropdowns.delete(this)
  }

  toggle() {
    this.menu?.classList.contains("show") ? this.hide() : this.show()
  }

  placement() {
    const parent = this.element.parentElement
    const end =
      this.menu?.classList.contains("dropdown-menu-end") ||
      getComputedStyle(this.menu).getPropertyValue("--cui-position").trim() === "end"
    const rtl = getComputedStyle(this.element).direction === "rtl"
    if (parent?.classList.contains("dropup")) return end ? "top-end" : "top-start"
    if (parent?.classList.contains("dropend")) return rtl ? "left-start" : "right-start"
    if (parent?.classList.contains("dropstart")) return rtl ? "right-start" : "left-start"
    return end ? "bottom-end" : "bottom-start"
  }

  dispose() {
    if (this.signal.aborted) return
    this.reset()
    super.dispose()
  }

  static clearMenus(except = null) {
    openDropdowns.forEach((instance) => {
      if (instance.element !== except) instance.hide()
    })
  }
}
